import { Link } from '@tanstack/react-router'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface MainHeaderProps {
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Унифицированный хедер для основных страниц
 * 
 * Используется на страницах с BottomNav:
 * - Главная
 * - Каталог  
 * - Дневник
 * - События
 * - Корзина
 * 
 * Отображает:
 * - Логотип приложения
 * - Аватар пользователя (ссылка на профиль)
 * 
 * @example
 * ```tsx
 * <MainHeader />
 * ```
 */
export function MainHeader({ className }: MainHeaderProps) {
  const { webApp } = useTelegram()
  const user = webApp?.initDataUnsafe?.user
  
  // Имя пользователя для аватара
  const userName = user?.first_name || 'U'
  const avatarUrl = user?.photo_url
  
  return (
    <header
      className={cn(
        'flex items-center justify-between',
        // Скрываем на desktop — там SideNav
        'lg:hidden',
        className
      )}
    >
      {/* Логотип */}
      <Link 
        to="/"
        className="flex items-center gap-2 touch-target"
      >
        <img 
          src="/logo.svg" 
          alt="Дракон и Сорока" 
          className="w-8 h-8 flex-shrink-0"
        />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
          Дракон и Сорока
        </span>
      </Link>
      
      {/* Аватар → Профиль */}
      <Link
        to="/profile"
        className={cn(
          'w-9 h-9 rounded-full overflow-hidden flex-shrink-0',
          'bg-gray-200 dark:bg-dark-elevated',
          'shadow-sm dark:shadow-md',
          'border-2 border-gray-300/60 dark:border-gray-500/50',
          'hover:border-primary/40 dark:hover:border-primary-dark/40',
          'transition-colors touch-target'
        )}
        aria-label="Профиль"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={userName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={cn(
            'w-full h-full flex items-center justify-center',
            'bg-primary/10 dark:bg-primary-dark/20',
            'text-primary dark:text-primary-light',
            'font-semibold text-sm'
          )}>
            {userName.charAt(0).toUpperCase()}
          </div>
        )}
      </Link>
    </header>
  )
}

